import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { NotificationsCommonActions } from '@capital-access/common/actions';
import {
  buildGroupedNotificationListRequest,
  NOTIFICATION_TYPE_CONFIGS
} from '../constants/notifications-drawer.constants';
import { NotificationsHubService } from '../services/notifications-hub.service';
import { NotificationsDrawerActions } from './notifications-drawer.actions';
import { selectRefillContext } from './notifications-drawer.selectors';

@Injectable()
export class NotificationsDrawerEffects {
  loadNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.loadNotifications),
      switchMap(() => {
        const initialLimits: Record<string, number> = {};
        for (const [code, config] of Object.entries(NOTIFICATION_TYPE_CONFIGS)) {
          initialLimits[code] = config.initialLimit;
        }
        return this.service.getGroupedNotifications(buildGroupedNotificationListRequest(initialLimits)).pipe(
          map(response => NotificationsDrawerActions.loadNotificationsSuccess({ response })),
          catchError(error => [
            NotificationsDrawerActions.loadNotificationsFailure({ error: error.message ?? 'Unknown error' })
          ])
        );
      })
    )
  );

  loadMoreForType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.loadMoreForType),
      withLatestFrom(this.store.select(selectRefillContext)),
      switchMap(([{ typeCode, refillCount }, { categories }]) => {
        const config = NOTIFICATION_TYPE_CONFIGS[typeCode];
        if (!config) {
          return [
            NotificationsDrawerActions.loadMoreForTypeFailure({
              typeCode,
              error: `Unknown notification type: ${typeCode}`
            })
          ];
        }

        const loadedCount =
          categories.flatMap(cat => cat.notificationsByType).find(nt => nt.notificationTypeCode === typeCode)
            ?.notifications.length ?? 0;

        if (refillCount != null) {
          // Targeted refill: fetch exactly refillCount items starting from offset=loadedCount.
          // With pageSize=refillCount, pageNumber=(loadedCount/refillCount)+1 gives
          // offset = (pageNumber-1) * pageSize = loadedCount exactly.
          // For refillCount=1 this simplifies to pageNumber = loadedCount + 1.
          const pageSize = refillCount;
          const pageNumber = Math.floor(loadedCount / refillCount) + 1;
          const appendCount = refillCount;
          return this.service
            .getNotificationsByType(config.notificationTypeId, config.withinDays, pageNumber, pageSize)
            .pipe(
              map(response => NotificationsDrawerActions.loadMoreForTypeSuccess({ typeCode, response, appendCount })),
              catchError(error => [
                NotificationsDrawerActions.loadMoreForTypeFailure({
                  typeCode,
                  error: error.message ?? 'Unknown error'
                })
              ])
            );
        }

        const pageSize = config.loadMoreLimit ?? config.initialLimit;
        const firstPageNum = Math.floor(loadedCount / pageSize) + 1;
        const overlap = loadedCount % pageSize;

        return this.service
          .getNotificationsByType(config.notificationTypeId, config.withinDays, firstPageNum, pageSize)
          .pipe(
            map(firstResponse => {
              // Remove items at the front of the page that are already in state (overlap).
              const newNotifications = firstResponse.notifications.slice(overlap);
              return NotificationsDrawerActions.loadMoreForTypeSuccess({
                typeCode,
                response: {
                  notifications: newNotifications,
                  totalCount: firstResponse.totalCount,
                  unreadCount: firstResponse.unreadCount
                }
              });
            }),
            catchError(error => [
              NotificationsDrawerActions.loadMoreForTypeFailure({
                typeCode,
                error: error.message ?? 'Unknown error'
              })
            ])
          );
      })
    )
  );

  /**
   * After a successful delete, if the type's locally-loaded buffer is now
   * shorter than the server total, fetch exactly 1 item to refill the visible
   * list without consuming extra quota from the user's next Show More click.
   */
  refillAfterDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.deleteNotificationSuccess),
      withLatestFrom(this.store.select(selectRefillContext)),
      filter(([{ typeCode }, { typePageStates }]) => !!typePageStates[typeCode]?.needsRefill),
      map(([{ typeCode }]) => NotificationsDrawerActions.loadMoreForType({ typeCode, refillCount: 1 }))
    )
  );

  markAsRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.markAsRead),
      mergeMap(({ notificationId }) =>
        this.service.markAsRead(notificationId).pipe(
          map(() => NotificationsDrawerActions.markAsReadSuccess({ notificationId })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  deleteNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsDrawerActions.deleteNotification),
      withLatestFrom(this.store.select(selectRefillContext)),
      mergeMap(([{ notificationId, typeCode }, { categories }]) => {
        const notification = categories
          .flatMap(cat => cat.notificationsByType)
          .find(nt => nt.notificationTypeCode === typeCode)
          ?.notifications.find(n => n.notificationId === notificationId);
        const wasUnread = notification ? !notification.read : false;
        return this.service.deleteNotification(notificationId).pipe(
          map(() => NotificationsDrawerActions.deleteNotificationSuccess({ notificationId, typeCode, wasUnread })),
          catchError(() => EMPTY)
        );
      })
    )
  );

  refreshDrawer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsCommonActions.refreshNotificationsDrawer),
      map(() => NotificationsDrawerActions.loadNotifications())
    )
  );

  constructor(private actions$: Actions, private store: Store, private service: NotificationsHubService) {}
}
